import type { Cache, ResolveInfo } from '@urql/exchange-graphcache';
import { type ResultOf, readFragment } from '@/gql/graphql';
import {
  type WatchPerson,
  WatchPersonFragment,
} from '@/hooks/useGlobalSubscriptions';
import syncNotificationBadge from '@/utils/syncNotificationBadge';
import { TEAM_PEOPLE_CACHE_FIELDS } from '../constants';
import invalidateFields from '../invalidateFields';

type ParticipationTimestamps = {
  chat: string | null;
  team: string | null;
};

const participationTimestampsByCache = new WeakMap<
  Cache,
  Map<string, ParticipationTimestamps>
>();

export default function current_user(
  ...vars: [
    parent: {
      current_user: ResultOf<typeof WatchPerson>['current_user'];
    },
    args: unknown,
    cache: Cache,
    info: ResolveInfo,
  ]
) {
  const [parent, , cache, info] = vars;

  if (!parent.current_user?.id) {
    return;
  }

  const person = readFragment(WatchPersonFragment, parent.current_user);
  const currentCountField = cache.keyOfField('unseen_message_count', {
    args: { exclude_chat_id: info.variables.excludeChatId ?? null },
  });
  for (const field of cache.inspectFields(person)) {
    if (
      field.fieldName === 'unseen_message_count' &&
      field.arguments &&
      field.fieldKey !== currentCountField
    ) {
      cache.invalidate(person, field.fieldKey);
    }
  }
  syncNotificationBadge(
    person?.notification_counts?.unseen_notification_count ?? 0,
  );

  let participationTimestampsByPerson =
    participationTimestampsByCache.get(cache);

  if (!participationTimestampsByPerson) {
    participationTimestampsByPerson = new Map();
    participationTimestampsByCache.set(cache, participationTimestampsByPerson);
  }

  const previousTimestamps = participationTimestampsByPerson.get(person.id);
  const nextTimestamps = {
    chat: person.chat_participation_updated_at,
    team: person.team_participation_updated_at,
  };
  participationTimestampsByPerson.set(person.id, nextTimestamps);

  if (!previousTimestamps) {
    return;
  }

  if (previousTimestamps.team !== nextTimestamps.team) {
    invalidateFields([...TEAM_PEOPLE_CACHE_FIELDS, 'chat_home'], cache);
    return;
  }

  if (previousTimestamps.chat !== nextTimestamps.chat) {
    invalidateFields(['chat_home'], cache);
  }
}
